import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
} from 'amazon-cognito-identity-js'
import { cognitoConfig } from '@/lib/cognito-config'
import { UserRole } from '@shared/types/roles'

const userPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.userPoolId,
  ClientId: cognitoConfig.clientId,
})

export interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  phone?: string
  dateOfBirth?: string
}

export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  role: UserRole
  status: string
}

export interface AuthResult {
  user: User
  token: string
  refreshToken: string
}

export const cognitoAuthService = {
  async register(data: RegisterData): Promise<void> {
    const attributes = [
      new CognitoUserAttribute({ Name: 'email', Value: data.email }),
      new CognitoUserAttribute({ Name: 'given_name', Value: data.firstName }),
      new CognitoUserAttribute({ Name: 'family_name', Value: data.lastName }),
      new CognitoUserAttribute({ Name: 'custom:role', Value: 'APPLICANT' }),
      new CognitoUserAttribute({ Name: 'custom:status', Value: 'REGISTERED' }),
    ]

    if (data.phone) {
      attributes.push(new CognitoUserAttribute({ Name: 'phone_number', Value: data.phone }))
    }

    if (data.dateOfBirth) {
      attributes.push(new CognitoUserAttribute({ Name: 'birthdate', Value: data.dateOfBirth }))
    }

    return new Promise((resolve, reject) => {
      userPool.signUp(data.email, data.password, attributes, [], (err, result) => {
        if (err) {
          reject(new Error(err.message))
          return
        }
        resolve()
      })
    })
  },

  async login(email: string, password: string): Promise<AuthResult> {
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    })

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    })

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session: CognitoUserSession) => {
          const idToken = session.getIdToken()
          const payload = idToken.payload

          const user: User = {
            id: payload.sub,
            email: payload.email,
            firstName: payload.given_name || '',
            lastName: payload.family_name || '',
            role: (payload['custom:role'] as UserRole) || 'APPLICANT',
            status: payload['custom:status'] || 'REGISTERED',
          }

          resolve({
            user,
            token: idToken.getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
          })
        },
        onFailure: (err) => {
          reject(new Error(err.message))
        },
      })
    })
  },

  async logout(): Promise<void> {
    const cognitoUser = userPool.getCurrentUser()
    if (cognitoUser) {
      cognitoUser.signOut()
    }
  },

  async getCurrentSession(): Promise<CognitoUserSession | null> {
    const cognitoUser = userPool.getCurrentUser()
    if (!cognitoUser) {
      console.debug('[Auth] No current user in pool')
      return null
    }

    return new Promise((resolve) => {
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          console.debug('[Auth] getSession error:', err.message)
          resolve(null)
          return
        }
        resolve(session)
      })
    })
  },

  async getCurrentUser(): Promise<User | null> {
    try {
      const session = await this.getCurrentSession()
      if (!session) return null

      const idToken = session.getIdToken()
      const payload = idToken.payload

      return {
        id: payload.sub,
        email: payload.email,
        firstName: payload.given_name || '',
        lastName: payload.family_name || '',
        role: (payload['custom:role'] as UserRole) || 'APPLICANT',
        status: payload['custom:status'] || 'REGISTERED',
      }
    } catch {
      return null
    }
  },

  async getToken(): Promise<string | null> {
    try {
      const session = await this.getCurrentSession()
      if (!session) return null
      return session.getIdToken().getJwtToken()
    } catch {
      return null
    }
  },
}
